import { CONNECTION_FAILURE_MESSAGE, getApiErrorUserMessage, readApiError } from "_shared/_errors/api-error";
import { isFetchBaseQueryError } from "_shared/_errors/api-error-message";
import { OVERSIZED_ATTACHMENT_ISSUE, UNSUPPORTED_ATTACHMENT_ISSUE } from "ai-assistant/attachments/assistant-attachment-policy";
import { HttpStatusCode } from "axios";

export const ATTACHMENT_UPLOAD_FAILED_ISSUE = "This file did not upload. Try again.";
/** What a turn refused over its files says when the server gave no sentence of its own. */
export const ATTACHMENT_NOT_SENDABLE_ISSUE = "This file can no longer be sent. Retry to attach it again, or remove it.";
/** The problem codes a turn is refused with when a file it carries cannot be sent. */
const TURN_ATTACHMENT_REFUSALS: ReadonlySet<string> = new Set(["attachment_not_found", "attachment_already_sent"]);
/** What a download answered 404 says: the file, or the conversation that held it, was deleted. */
export const ATTACHMENT_GONE_ISSUE = "This file is no longer available. It may have been deleted with its conversation.";

/**
 * What to say beside a file whose upload was refused or failed (#6504).
 *
 * The server's own sentence where it gave one: it read the content, and "the file's content does
 * not match its type" is something only it can know. A refusal without a body - a proxy's 413, say -
 * is named by its status, and anything else says to try again.
 */
export function attachmentUploadIssue(error: unknown): string {
  if (!isFetchBaseQueryError(error)) return ATTACHMENT_UPLOAD_FAILED_ISSUE;
  const result = readApiError(error);
  if (result.category === "connection") return CONNECTION_FAILURE_MESSAGE;
  if (result.source === "problem-details") return getApiErrorUserMessage(result) ?? ATTACHMENT_UPLOAD_FAILED_ISSUE;
  if (error.status === HttpStatusCode.PayloadTooLarge) return OVERSIZED_ATTACHMENT_ISSUE;
  if (error.status === HttpStatusCode.UnsupportedMediaType) return UNSUPPORTED_ATTACHMENT_ISSUE;
  return ATTACHMENT_UPLOAD_FAILED_ISSUE;
}

/**
 * Why a turn was refused over the files it carried, or nothing when the refusal was not about them
 * (#6504 review).
 *
 * Two refusals are the files' fault, and attaching them again puts both right: a file that is gone
 * (the sweep took it after a day unsent, or its conversation was deleted) is 404
 * `attachment_not_found`, and one already sent - by a turn whose answer never arrived - is 409
 * `attachment_already_sent`. A bare 404 is a conversation that is gone, which attaching again would
 * not help, so it is not one of these.
 */
export function turnAttachmentRefusal(error: unknown): string | undefined {
  if (!isFetchBaseQueryError(error)) return undefined;
  const result = readApiError(error);
  if (result.code === undefined || !TURN_ATTACHMENT_REFUSALS.has(result.code)) return undefined;
  return getApiErrorUserMessage(result) ?? ATTACHMENT_NOT_SENDABLE_ISSUE;
}
